<template>
  <div class="px-3">
    <h5 class="text-primary font-weight-bold pb-2">Search Notifications</h5>
    <b-form>
      <label for="searchBy" class="sr-only">Search by</label>
      <label for="searchValue" class="sr-only">{{ selectedFilter.text }}</label>
      <b-input-group class="input-group-stacked">
        <b-input-group-prepend>
          <b-form-select
            id="searchBy"
            v-model="selectedOption"
            plain
            :options="options"
            class="rounded-right-0 my-3 my-lg-auto"
            @change="filterOptionChanged"
          />
        </b-input-group-prepend>
        <b-form-input
          v-if="selectedOption === 'roads'"
          id="searchValue"
          v-model="currentValue"
          class="my-3 my-lg-auto"
          placeholder="Enter Road Name"
        />
        <b-form-input
          v-if="selectedOption === 'participants'"
          id="searchValue"
          v-model="currentValue"
          class="my-3 my-lg-auto"
          placeholder="Enter Participant Name"
        />
        <b-form-input
          v-if="selectedOption === 'foresters'"
          id="searchValue"
          v-model="currentValue"
          class="my-3 my-lg-auto"
          placeholder="Enter Forester Name"
        />
        <b-form-input
          v-if="selectedOption === 'rangers'"
          id="searchValue"
          v-model="currentValue"
          class="my-3 my-lg-auto"
          placeholder="Enter Ranger Name"
        />
        <b-form-input
          v-if="selectedOption === 'forestIds'"
          id="searchValue"
          v-model="currentValue"
          class="my-3 my-lg-auto"
          placeholder="Enter FOResT ID#"
        />
        <b-form-select
          v-if="selectedOption === 'towns'"
          id="searchValue"
          v-model="currentValue"
          class="my-3 my-lg-auto"
          plain>
          <option :value="null" disabled>Select a Town</option>
          <option
            v-for="option in availableTowns"
            :key="option.geocodeNum"
            :value="option.geocodeNum"
          >
            {{option.name}}
          </option>
        </b-form-select>
        <b-form-select
          v-if="selectedOption === 'certified'"
          id="searchValue"
          v-model="currentValue"
          class="my-3 my-lg-auto"
          plain>
          <option :value="null" disabled>Select a Time Period</option>
          <option
            v-for="option in certifiedOptions"
            :key="option.value"
            :value="option.value"
          >
            {{option.name}}
          </option>
        </b-form-select>
        <b-form-select
          v-if="selectedOption === 'activity'"
          id="searchValue"
          v-model="currentValue"
          class="my-3 my-lg-auto"
          plain>
          <option :value="null" disabled>Select an Activity</option>
          <option
            v-for="option in availableActivities"
            :key="option.value"
            :value="option.value"
          >
            {{option.name}}
          </option>
        </b-form-select>
        <b-form-select
          v-if="selectedOption === 'statusIds'"
          id="searchValue"
          v-model="currentValue"
          plain
          class="my-3 my-lg-auto">
          <option :value="null" disabled>Select a Status</option>
          <option
            v-for="option in availableStatuses"
            :key="option.id"
            :value="option.id"
          >
            {{option.name}}
          </option>
        </b-form-select>
        <b-form-datepicker
          v-if="selectedFilter.type === Date"
          id="searchValue"
          name="date"
          v-model="currentValue"
          class="my-3 my-lg-auto"
          ref="datepicker"
          placeholder="Choose a date"
          local="en"
        >
          <template #button-content>
            <FontAwesomeIcon icon="calendar-alt" aria-hidden="true" />
            <span class="sr-only">Choose a date</span>
          </template>
        </b-form-datepicker>
        <template v-slot:append>
          <b-button
            class="add-filter my-3 my-lg-auto"
            @click="addFilter"
            :disabled="duplicateFilter || !currentValue || searchFilters.length >= 10"
            aria-label="Add filter"
          >
            + Add Filter
          </b-button>
        </template>
      </b-input-group>
    </b-form>
    <b-row no-gutters class="my-1">
      <div
        v-for="filter in searchFilters"
        :key="filter.key + filter.value"
        :class="filterClasses"
      >
        {{filterString(filter)}}
        <span class="c-pointer ml-1" @click="removeFilter(filter)">
          x
        </span>
      </div>
    </b-row>
    <div v-if="searchFilters.length" class="my-3 mt-lg-auto justify-content-center d-flex">
      <a class="c-pointer text-secondary" @click="clearAll()">Clear All Filters</a>
    </div>
    <SearchList :searchFilters="searchFilters" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import LookupModules from '@/store/moduleConfig';
import SearchList from '@/components/dashboard/SearchList.vue';

export default {
  name: 'Search',
  components: {
    SearchList,
  },
  data() {
    return {
      selectedOption: 'participants',
      currentValue: null,
      options: [
        { value: 'participants', text: 'Participants', multiple: true },
        { value: 'foresters', text: 'District Forester', multiple: true },
        { value: 'rangers', text: 'Ranger', multiple: true },
        { value: 'forestIds', text: 'FOResT ID#', multiple: true },
        { value: 'towns', text: 'Town', multiple: true },
        { value: 'roads', text: 'Road', multiple: true },
        { value: 'certified', text: 'Certified', multiple: false },
        { value: 'activity', text: 'Activity', multiple: true },
        {
          value: 'startFrom', text: 'Operation Start (from)', type: Date, multiple: false,
        },
        {
          value: 'startTo', text: 'Operation Start (to)', type: Date, multiple: false,
        },
        {
          value: 'endFrom', text: 'Operation End (from)', type: Date, multiple: false,
        },
        {
          value: 'endTo', text: 'Operation End (to)', type: Date, multiple: false,
        },
        { value: 'statusIds', text: 'Status', multiple: true },
      ],
      certifiedOptions: [
        { value: 'today', name: 'Today' },
        { value: 'yesterday', name: 'Yesterday' },
        { value: 'lastWeek', name: 'In the last week' },
      ],
      activityOptions: [
        { value: 'harvest', name: 'Harvest' },
        { value: 'road_construction', name: 'Road Construction' },
        { value: 'water_crossing', name: 'Water Crossing' },
      ],
      searchFilters: [],
      filterClasses: 'rounded-pill d-flex justify-content-between w-100 w-lg-auto border border-primary my-2 mr-2 font-md px-2 py-1 text-primary',
    };
  },
  created() {
    this.$store.dispatch(`${LookupModules.TOWNS.namespace}/fetchOptions`);
    this.$store.dispatch(`${LookupModules.NOTIFICATION_STATUSES.namespace}/fetchOptions`);
    if (this.$route.query.search) this.searchFilters = JSON.parse(this.$route.query.search);
  },
  computed: {
    ...mapState(LookupModules.TOWNS.namespace, {
      townOptions: state => state.options,
      townsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.NOTIFICATION_STATUSES.namespace, {
      statusOptions: state => state.options,
      statusLoading: state => state.loading,
    }),
    ...mapGetters(LookupModules.TOWNS.namespace, {
      getTownName: 'getName',
    }),
    ...mapGetters(LookupModules.NOTIFICATION_STATUSES.namespace, {
      getStatusName: 'getName',
    }),
    availableStatuses() {
      return this.statusOptions
        .filter(to => !this.searchFilters
          .filter(sf => sf.key === 'statusIds')
          .some(sf => sf.value === to.id));
    },
    availableTowns() {
      return this.townOptions
        .filter(to => !this.searchFilters
          .filter(sf => sf.key === 'towns')
          .some(sf => sf.value === to.geocodeNum));
    },
    availableActivities() {
      return this.activityOptions
        .filter(to => !this.searchFilters
          .filter(sf => sf.key === 'activity')
          .some(sf => sf.value === to.value));
    },
    selectedFilter() {
      return this.options.find(o => o.value === this.selectedOption);
    },
    currentSearchFilter() {
      return {
        key: this.selectedFilter.value,
        value: this.currentValue,
      };
    },
    duplicateFilter() {
      const multiple = !this.selectedFilter.multiple
          && this.searchFilters.some(sf => sf.key === this.currentSearchFilter.key);
      return this.searchFilters
        .some(sf => (
          sf.key === this.currentSearchFilter.key && sf.value === this.currentSearchFilter.value
        ))
        || multiple;
    },
  },
  watch: {
    searchFilters() {
      const searchQuery = JSON.stringify(this.searchFilters);
      if (this.$route.query.search !== searchQuery) {
        this.$router.push({ path: 'dashboard', query: { search: searchQuery } });
      }
    },
  },
  methods: {
    addFilter() {
      this.searchFilters.push(this.currentSearchFilter);
      this.currentValue = null;
    },
    certifiedName(value) {
      return this.certifiedOptions.find(co => co.value === value).name;
    },
    activityName(value) {
      return this.activityOptions.find(ao => ao.value === value).name;
    },
    clearAll() {
      this.searchFilters = [];
    },
    filterOptionChanged() {
      this.currentValue = null;
    },
    filterString(filter) {
      const displayMap = [
        { key: 'towns', displayFunction: this.getTownName },
        { key: 'statusIds', displayFunction: this.getStatusName },
        { key: 'certified', displayFunction: this.certifiedName },
        { key: 'activity', displayFunction: this.activityName },
      ];

      const option = this.options.find(o => o.value === filter.key);
      const displayEntry = displayMap.find(de => de.key === filter.key);

      let displayValue = filter.value;
      if (displayEntry) displayValue = displayEntry.displayFunction(filter.value);
      return `${option.text}: ${displayValue}`;
    },
    removeFilter(filter) {
      const index = this.searchFilters.indexOf(filter);
      this.searchFilters.splice(index, 1);
    },
  },
};
</script>
